import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './COwnerAdminClientes.css';
import Spinner from './Spinner';
import axios from 'axios';
import DataTable, { ActionMenu } from './DataTable';
import { Icons } from './ActionIcons';

type Cliente = {
  _id?: string;
  nome: string;
  descricao?: string;
  createdBy?: { fullName?: string; username?: string } | string;
  createdAt?: string;
  updatedAt?: string;
};

const COwnerAdminClientes: React.FC = () => {
  const navigate = useNavigate();
  
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Cliente | null>(null);
  const [creating, setCreating] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showModal, setShowModal] = useState(false);

  // Toast notifications
  const [toast, setToast] = useState<{ visible: boolean; message: string; variant?: 'info' | 'danger' | 'success' }>({ visible: false, message: '', variant: 'info' });
  
  // Confirm dialog
  const [confirmState, setConfirmState] = useState<{ visible: boolean; title?: string; message?: string; onConfirm?: () => void }>({ visible: false });

  // theme handled globally in AppLayout

  // Filtered and sorted clients (same model as Gerador de Perfis DataTable)
  const filteredClientes = useMemo(() => {
    let list = clientes.slice();

    if (Object.keys(columnFilters).length > 0) {
      list = list.filter(c => {
        return Object.entries(columnFilters).every(([key, value]) => {
          if (!value) return true;
          const lower = value.toLowerCase();
          const createdByText = c.createdBy ? (typeof c.createdBy === 'string' ? c.createdBy : (c.createdBy.fullName || c.createdBy.username || '')) : '';
          const cellValue = String(
            key === 'createdBy' ? createdByText :
            key === 'createdAt' || key === 'updatedAt' ? (c[key as keyof Cliente] || '') :
            (c as any)[key] || ''
          ).toLowerCase();
          return cellValue.includes(lower);
        });
      });
    }

    list.sort((a, b) => {
      const getVal = (row: Cliente, c: string) => {
        if (c === 'createdBy') return row.createdBy ? (typeof row.createdBy === 'string' ? row.createdBy : (row.createdBy.fullName || row.createdBy.username || '')) : '';
        return (row as any)[c] || '';
      };

      const va = getVal(a, sortColumn);
      const vb = getVal(b, sortColumn);
      if ((sortColumn === 'createdAt' || sortColumn === 'updatedAt') && va && vb) {
        return sortDir === 'asc' ? new Date(va).getTime() - new Date(vb).getTime() : new Date(vb).getTime() - new Date(va).getTime();
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'pt-BR')
        : String(vb).localeCompare(String(va), 'pt-BR');
    });

    return list;
  }, [clientes, columnFilters, sortColumn, sortDir]);

  const paginatedClientes = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredClientes.slice(start, start + pageSize);
  }, [filteredClientes, currentPage, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredClientes.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredClientes.length, pageSize, currentPage]);

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash ? `${baseNoSlash}/api/cowner-clientes` : '/api/cowner-clientes';
    setLoading(true);
    axios.get(url, { withCredentials: true })
      .then(res => {
        setClientes(Array.isArray(res.data) ? res.data : []);
        setError(null);
      })
      .catch(err => {
        setError(err?.response?.data?.message || err.message || 'Erro ao carregar clientes');
        setClientes([]);
      })
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditing({ nome: '', descricao: '' });
    setCreating(true);
    setShowModal(true);
  };

  const openEdit = (c: Cliente) => {
    setEditing({ ...c });
    setCreating(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const saveCliente = async () => {
    if (!editing) return;
    if (!editing.nome || !editing.nome.trim()) {
      showToast('Nome é obrigatório', 'danger');
      return;
    }
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const baseNoSlash = base ? base.replace(/\/$/, '') : '';

      if (creating) {
        const url = baseNoSlash ? `${baseNoSlash}/api/cowner-clientes` : '/api/cowner-clientes';
        await axios.post(url, editing, { withCredentials: true });
        showToast('Cliente criado com sucesso', 'success');
      } else {
        const url = baseNoSlash ? `${baseNoSlash}/api/cowner-clientes/${editing._id}` : `/api/cowner-clientes/${editing._id}`;
        await axios.put(url, editing, { withCredentials: true });
        showToast('Cliente atualizado com sucesso', 'success');
      }
      closeModal();
      loadClientes();
    } catch (err: any) {
      showToast(err?.response?.data?.message || err.message || 'Erro ao salvar cliente', 'danger');
    }
  };

  const deleteCliente = (c: Cliente) => {
    showConfirm(
      'Excluir Cliente',
      `Tem certeza que deseja excluir o cliente "${c.nome}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const base = process.env.REACT_APP_API_URL || '';
          const baseNoSlash = base ? base.replace(/\/$/, '') : '';
          const url = baseNoSlash ? `${baseNoSlash}/api/cowner-clientes/${c._id}` : `/api/cowner-clientes/${c._id}`;
          await axios.delete(url, { withCredentials: true });
          showToast('Cliente excluído com sucesso', 'success');
          loadClientes();
        } catch (err: any) {
          showToast(err?.response?.data?.message || err.message || 'Erro ao excluir cliente', 'danger');
        }
      }
    );
  };

  const showToast = (message: string, variant: 'info' | 'danger' | 'success' = 'info') => {
    setToast({ visible: true, message, variant });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 4000);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmState({ visible: true, title, message, onConfirm });
  };

  const hideConfirm = () => setConfirmState({ visible: false });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const getCreatedByName = (createdBy?: { fullName?: string; username?: string } | string) => {
    if (!createdBy) return '-';
    if (typeof createdBy === 'string') return createdBy;
    return createdBy.fullName || createdBy.username || '-';
  };

  const handleRowClick = (cliente: Cliente) => {
    if (cliente.nome) {
      navigate(`/cowner-cliente/${encodeURIComponent(cliente.nome)}`);
    }
  };

  if (loading) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  return (
    <>
      
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="mb-0">Administrar Clientes</h1>
            <p className="lead mb-0">Gerencie o cadastro de clientes do Compliance Owner</p>
          </div>
          <div>
            <button className="btn btn-secondary cowner-uniform-btn" onClick={openCreate}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
              </svg>
              <span className="ms-2">Novo Cliente</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <DataTable
            columns={[
              {
                key: 'nome',
                label: 'Nome',
                sortable: true,
                render: (value: any) => <span className="fw-bold">{value || '-'}</span>
              },
              { key: 'descricao', label: 'Descrição', sortable: true, render: (value: any) => value || '-' },
              {
                key: 'createdBy',
                label: 'Criado por',
                sortable: true,
                render: (_value: any, row: Cliente) => getCreatedByName(row.createdBy)
              },
              {
                key: 'createdAt',
                label: 'Criado em',
                sortable: true,
                render: (value: any) => formatDate(value)
              },
              {
                key: 'actions',
                label: 'Ações',
                sortable: false,
                minWidth: 100,
                render: (_value: any, row: Cliente) => (
                  <ActionMenu
                    actions={[
                      {
                        key: 'edit',
                        label: 'Editar',
                        icon: Icons.Pencil,
                        onClick: () => openEdit(row)
                      },
                      {
                        key: 'open',
                        label: 'Abrir cliente',
                        icon: Icons.ArrowRight,
                        onClick: () => handleRowClick(row)
                      },
                      {
                        key: 'delete',
                        label: 'Excluir',
                        icon: Icons.Trash,
                        onClick: () => deleteCliente(row),
                        variant: 'danger'
                      }
                    ]}
                  />
                )
              }
            ]}
            data={paginatedClientes}
            keyExtractor={(row: Cliente, index: number) => row._id || `${row.nome}-${index}`}
            loading={loading}
            sortColumn={sortColumn}
            sortDirection={sortDir}
            onSort={(column: string, direction?: 'asc' | 'desc') => {
              if (direction) {
                setSortColumn(column);
                setSortDir(direction);
              } else if (sortColumn === column) {
                setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
              } else {
                setSortColumn(column);
                setSortDir('asc');
              }
              setCurrentPage(1);
            }}
            filters={columnFilters}
            onFilter={(columnKey: string, value: string) => {
              setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
              setCurrentPage(1);
            }}
            onRowClick={(row: Cliente) => handleRowClick(row)}
            striped
            hover
            responsive
            emptyMessage="Nenhum cliente encontrado"
            pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={filteredClientes.length}
            onPageChange={(page: number) => setCurrentPage(page)}
            onPageSizeChange={(size: number) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />

        <div className="cowner-admin-footer">
          <p className="text-muted mb-0">
            {filteredClientes.length} {filteredClientes.length === 1 ? 'cliente' : 'clientes'}
          </p>
        </div>
      </div>

      {/* Modal de Criar/Editar */}
      {showModal && editing && (
        <div className="cowner-modal-overlay" onClick={closeModal}>
          <div className="cowner-modal" onClick={e => e.stopPropagation()}>
            <div className="cowner-modal-header">
              <h3>{creating ? 'Novo Cliente' : 'Editar Cliente'}</h3>
              <button className="cowner-modal-close" onClick={closeModal} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="cowner-modal-body">
              <div className="mb-3">
                <label className="form-label">Nome <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={editing.nome}
                  onChange={e => setEditing({ ...editing, nome: e.target.value })}
                  placeholder="Nome do cliente"
                  autoFocus
                />
                {creating && (
                  <small className="text-muted">
                    Este nome será usado na URL do cliente
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Descrição</label>
                <textarea
                  className="form-control"
                  value={editing.descricao || ''}
                  onChange={e => setEditing({ ...editing, descricao: e.target.value })}
                  placeholder="Descrição do cliente (opcional)"
                  rows={4}
                />
              </div>
            </div>
            <div className="cowner-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={saveCliente}>
                {creating ? 'Criar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`cowner-toast cowner-toast-${toast.variant}`}>
          {toast.message}
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmState.visible && (
        <div className="cowner-modal-overlay" onClick={hideConfirm}>
          <div className="cowner-modal cowner-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="cowner-modal-header">
              <h3>{confirmState.title}</h3>
              <button className="cowner-modal-close" onClick={hideConfirm} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="cowner-modal-body">
              <p>{confirmState.message}</p>
            </div>
            <div className="cowner-modal-footer">
              <button className="btn btn-secondary" onClick={hideConfirm}>
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  confirmState.onConfirm?.();
                  hideConfirm();
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default COwnerAdminClientes;
